// @ts-nocheck
/** *****
 * Store Custom Code:
 * For updates, compare with: modules\content-library\src\Components\OrderLinesList.tsx
 * ****** */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import CardContainer from "@insite/content-library/Components/CardContainer";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Modal from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import { OrderLineHeaderStyles } from "@store/Library/Components/OrderLineHeader";
import OrderLineTrackingInfo from "@store/Library/Components/OrderLineTrackingInfo";
import CartLinesListHeader from "@store/Overrides/Components/CartLinesListHeader";
import ProductPartNumbers from "@store/Overrides/Components/ProductPartNumbers";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Store Custom Code - End */

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
    orderDetailsParameter: state.store.pages.orderDetails.orderDetailsParameter, // Store Custom Code
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: CardContainerStyles; // Store Custom Code
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    // Store Custom Code
    productImage?: ProductImageStyles;
    orderLineCardInfoNoActionGridItem?: GridItemProps;
    orderLineHeader?: OrderLineHeaderStyles;
    wrapperOrderLinesList?: InjectableCss;
    productInfoHeadingAndTextLabel?: TypographyProps;
    orderLineCustomPropertiesGridItem?: GridItemProps;
    productInfoTextGridItem?: GridItemProps;
    productInfoTextContent?: InjectableCss;
    productInfoTextContentHighlighted?: InjectableCss;
    orderLineCustomPropertiesGridContainer?: GridContainerProps;
    orderLineTableContainer?: InjectableCss;
    productInfoWrapperGridItem?: GridItemProps;
    productInfoNoPropertiesWrapperGridItem?: GridItemProps;
    orderLineCustomPropertiesWrapperGridItem?: GridItemProps;
    orderLineCustomPropertiesEmptyWrapperGridItem?: GridItemProps;
    productSubtotalWrapperGridItem?: GridItemProps;
    orderLineUnitPrice?: InjectableCss;
    orderLineUnitOfMesure?: InjectableCss;
    productInfoSubtotalHeading?: InjectableCss;
    productInfoSubtotalPrice?: InjectableCss;
    routingType?: InjectableCss;
    routingTypeHighlighted?: InjectableCss;
    orderLineTrackingGridItem?: GridItemProps;
    trackOrderLineButton?: ButtonPresentationProps;
    orderLineProductInfoGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    messageText?: TypographyPresentationProps;
}

/* Store Custom Code - Start */
const productInfoTextContentStyles = () => {
    return css`
        width: 100%;
        padding: ${pxToRem(5, 15)};
    `;
};

const orderLineCardSpacing = 20;
/* Store Custom Code - End */

export const orderLinesListStyles: OrderLinesListStyles = {
    /* Store Custom Code - Start */
    wrapperOrderLinesList: {
        css: css`
            margin-top: 2rem;
        `,
    },
    gridContainer: {
        gap: 0,
    },
    orderLineCardContainer: {
        cardDivider: {
            css: css`
                width: 100%;
                margin: 0;
                border-bottom: 1px solid ${getColor("secondary")};

                ${breakpoint("lg", "up")} {
                    border-color: ${getColor("common.border")};
                }
            `,
        },
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardGridContainer: {
        css: css`
            width: 100%;
        `,
        gap: 0,
    },
    productInfoWrapperGridItem: {
        width: [12, 7, 7, 6, 6],
    },
    productInfoNoPropertiesWrapperGridItem: {
        width: [12, 12, 12, 10, 10],
    },
    orderLineCardImageGridItem: {
        width: [3, 3, 4, 4, 4],
    },
    productInfoGridItem: {
        width: [9, 9, 8, 8, 8],
        printWidth: 12,
    },
    productInfoGridContainer: {
        gap: 0,
        css: css`
            padding: ${pxToRem(orderLineCardSpacing, 10, orderLineCardSpacing, 0)};
        `,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
    },
    productInfoDescriptionGridItem: {
        width: 12,
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
    },
    productInfoErpNumberGridItem: {
        width: 12,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 12, 8, 8],
    },
    orderLinePriceGridItem: {
        width: 12,
        css: css`
            color: ${getColor("primary")};
            align-items: center;
            margin: ${pxToRem(5, 0)};

            @media print {
                display: none !important;
            }
        `,
    },
    orderLineUnitPrice: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    orderLineUnitOfMesure: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    productInfoQtyOrderedGridItem: {
        width: 12,
        css: css`
            padding-top: ${pxToRem(10)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    productInfoQtyShippedGridItem: {
        width: 12,
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    orderLineCustomPropertiesWrapperGridItem: {
        width: [12, 5, 5, 4, 4],
        css: css`
            background-color: ${getColor("common.accent")};
            padding: ${pxToRem(orderLineCardSpacing, 0)};

            @media print {
                background: none;
            }
        `,
    },
    orderLineCustomPropertiesEmptyWrapperGridItem: {
        width: [12, 5, 5, 4, 4],
    },
    orderLineCustomPropertiesGridContainer: {
        gap: 0,
    },
    productInfoTextGridItem: {
        width: 12,
        printWidth: 12,
        css: css`
            flex-direction: column;
            font-size: ${x => x.theme.design.typography.size.smallest};
        `,
    },
    productInfoTextContent: {
        css: css`
            ${productInfoTextContentStyles}
        `,
    },
    productInfoTextContentHighlighted: {
        css: css`
            ${productInfoTextContentStyles}
            background-color: ${getColor("danger.contrast")};
        `,
    },
    productInfoHeadingAndTextLabel: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    productSubtotalWrapperGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            padding: ${pxToRem(orderLineCardSpacing, 0)};
            justify-content: flex-end;
            align-items: center;

            ${breakpoint("md", "down")} {
                padding: ${pxToRem(5, 0)};
                border-top: 1px solid ${getColor("common.border")};
            }

            ${breakpoint("lg", "up")} {
                display: block;
                text-align: right;
            }

            @media print {
                display: none !important;
            }
        `,
    },
    productInfoSubtotalHeading: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.small};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    productInfoSubtotalPrice: {
        css: css`
            color: ${getColor("primary")};
            font-size: ${x => x.theme.design.typography.size.large};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    orderLineTrackingGridItem: {
        width: 12,
        css: css`
            padding-top: ${pxToRem(20)};

            @media print {
                display: none;
            }
        `,
    },
    trackOrderLineButton: {
        sizeVariant: "small",
    },
    orderLineProductInfoGridItem: {
        width: 12,
    },
    centeringWrapper: {
        css: css`
            height: ${pxToRem(300)};
            display: flex;
            align-items: center;
        `,
    },
    messageText: {
        variant: "h4",
        forwardAs: "h2",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    /* Store Custom Code - End */
};

const OrderLineProductInfo = ({
    orderLine,
    styles,
    vmiLocationId,
    productInfoHeadingAndTextLabel, // Store Custom Code
}: {
    orderLine: OrderLineModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
    productInfoHeadingAndTextLabel: any; // Store Custom Code
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <li {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...styles.productInfoSectionOptionsText}>
                    {option.sectionName}: {option.optionName}
                </Typography>
            </li>
        ));
    }

    return (
        <GridItem {...styles.orderLineProductInfoGridItem} /* Store Custom Code - change class */>
            <GridContainer {...styles.productInfoGridContainer}>
                <GridItem {...styles.productInfoBrandDescriptionGridItem}>
                    <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                        {orderLine.brand && (
                            <GridItem {...styles.productInfoBrandGridItem}>
                                <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />
                            </GridItem>
                        )}
                        <GridItem {...styles.productInfoDescriptionGridItem}>
                            {vmiLocationId && orderLine.isActiveVmiBin && (
                                <VmiBinDetailTypeLink
                                    title={orderLine.shortDescription}
                                    locationId={vmiLocationId}
                                    id={orderLine.vmiBinId}
                                />
                            )}
                            {vmiLocationId && !orderLine.isActiveVmiBin && (
                                <Typography as="p">{orderLine.shortDescription}</Typography>
                            )}
                            {!vmiLocationId && (
                                <ProductDescription
                                    product={orderLine}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {sectionOptions.length > 0 && (
                    <GridItem {...styles.productInfoSectionOptionsGridItem}>
                        <ul {...styles.productInfoSectionOptionsList}>{sectionOptions}</ul>
                    </GridItem>
                )}
                <GridItem {...styles.productInfoPartNumbersGridItem}>
                    <GridContainer {...styles.productInfoPartNumbersGridContainer}>
                        {/* Store Custom Code - Removed customerProductNumber */}
                        <GridItem {...styles.productInfoErpNumberGridItem}>
                            {/* Store Custom Code - Remove SmallHeadingAndText orderLine.manufacturerItem and SmallHeadingAndText orderLine.customerProductNumber */}
                            {/* Store Custom Code - Start */}
                            <ProductPartNumbers
                                productNumber={orderLine.productErpNumber}
                                customerProductNumber={orderLine.customerProductNumber || orderLine.customerName}
                                manufacturerItem={orderLine.manufacturerItem}
                                glNumber={orderLine.properties.glNumber}
                            />
                            {/* Store Custom Code - End */}
                        </GridItem>
                        {/* Store Custom Code - Removed manufacturerItem */}
                        {orderLine.vmiBinNumber && (
                            <GridItem {...styles.productInfoVmiBinNumberGridItem}>
                                <SmallHeadingAndText
                                    {...styles.vmiBinNumberHeadingAndText}
                                    heading={translate("Bin #")}
                                    text={orderLine.vmiBinNumber}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                {/* Store Custom Code - Start */}
                <GridItem {...styles.orderLinePriceGridItem}>
                    <Typography as="span" {...styles.orderLineUnitPrice}>
                        {orderLine.unitRegularPriceDisplay}
                    </Typography>
                    {orderLine.unitOfMeasure && (
                        <Typography as="span" {...styles.orderLineUnitOfMesure}>
                            &nbsp; {` / ${orderLine.unitOfMeasure}`}
                        </Typography>
                    )}
                </GridItem>
                <GridItem {...styles.productInfoQtyOrderedGridItem}>
                    <Typography as="span" {...styles.productInfoHeadingAndTextLabel}>
                        {productInfoHeadingAndTextLabel.qtyOrderedLabel}:
                    </Typography>
                    <Typography as="span" {...styles.productInfoQtyOrderedHeadingAndText}>
                        &nbsp; {orderLine.qtyOrdered}
                    </Typography>
                </GridItem>
                <GridItem {...styles.productInfoQtyShippedGridItem}>
                    <Typography as="span" {...styles.productInfoHeadingAndTextLabel}>
                        {productInfoHeadingAndTextLabel.qtyShippedLabel}:
                    </Typography>
                    <Typography as="span" {...styles.productInfoQtyShippedHeadingAndText}>
                        &nbsp; {orderLine.qtyShipped}
                    </Typography>
                </GridItem>
                {/* Store Custom Code - End */}
            </GridContainer>
        </GridItem>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const OrderLineInfoProto = ({
    orderLine,
    order,
    enableVat,
    vatPriceDisplay,
    styles,
    showPricing,
    productInfoHeadingAndTextLabel, // Store Custom Code
}: {
    orderLine: OrderLineModel;
    order: OrderModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
    showPricing?: boolean;
    productInfoHeadingAndTextLabel: any; // Store Custom Code
}) => {
    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }

    return (
        /* Store Custom Code - Start */
        <>
            <GridItem {...styles.orderLineInfoGridItem}>
                <GridContainer {...styles.orderLineInfoGridContainer}>
                    <GridItem {...styles.productInfoTotalsGridItem}>
                        <GridContainer {...styles.productInfoTotalsGridContainer}></GridContainer>
                    </GridItem>

                    {promotions.length > 0 && (
                        <GridItem {...styles.orderLineInfoPromotionGridItem}>
                            <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </>
        /* Store Custom Code - End */
    );
};

const OrderLineInfo = connect(mapStateToOrderLineInfoProps)(OrderLineInfoProto);

const OrderLineNotes = ({
    orderLine,
    extendedStyles,
}: {
    orderLine: OrderLineModel;
    extendedStyles?: OrderLineNotesStyles;
}) => {
    return orderLine.notes ? (
        <GridItem {...extendedStyles?.gridItemStyles}>
            <Typography {...extendedStyles?.typographyStyles}>{orderLine.notes}</Typography>
        </GridItem>
    ) : null;
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    productInfoHeadingAndTextLabel: any; // Store Custom Code
}) => {
    const {
        orderLine,
        order,
        wishListSettings,
        vmiBinDetailsPageUrl,
        styles,
        setAddToListModalIsOpen,
        addToWishList,
        productInfoHeadingAndTextLabel, // Store Custom Code
    } = props;
    const toasterContext = React.useContext(ToasterContext);
    const [isTrackingModalOpen, setIsTrackingModalOpen] = React.useState(false); // Store Custom Code
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const isShareEntity = getIsShareEntity();

    /* Store Custom Code - Start */
    const onClickTracking = () => {
        setIsTrackingModalOpen(!isTrackingModalOpen);
    };

    const getOrderLineTracking = () => {
        onClickTracking();
    };

    const orderLineTracking = order.shipmentPackages?.filter(element =>
        element.shipmentPackageLineDtos?.some(e => e.orderLineId === orderLine.id),
    );

    const hasCustomProperties =
        orderLine.status.length > 0 ||
        orderLine.warehouse.length > 0 ||
        orderLine.properties.originalReleaseDate !== undefined ||
        orderLine.properties.inStoreDate.length > 0 ||
        orderLine.properties.releaseDate.length > 0 ||
        orderLine.properties.routingType !== undefined ||
        orderLine.properties.estimatedDeliveryDate.length > 0;

    /* Store Custom Code - End */

    return (
        /* Store Custom Code - Start */
        <GridContainer {...styles.orderLineCardGridContainer}>
            <GridItem
                {...(hasCustomProperties
                    ? styles.productInfoWrapperGridItem
                    : styles.productInfoNoPropertiesWrapperGridItem)}
            >
                <GridContainer>
                    <GridItem {...styles.orderLineCardImageGridItem}>
                        <ProductImage product={orderLine as any} extendedStyles={styles.productImage} />
                    </GridItem>
                    <GridItem {...styles.productInfoGridItem}>
                        <GridContainer {...styles.productInfoGridContainer}>
                            <OrderLineProductInfo
                                orderLine={orderLine}
                                styles={styles}
                                productInfoHeadingAndTextLabel={productInfoHeadingAndTextLabel}
                                vmiLocationId={order.vmiLocationId}
                            />
                            {orderLineTracking !== undefined && orderLineTracking.length > 0 && (
                                <GridItem {...styles.orderLineTrackingGridItem}>
                                    <Button onClick={() => getOrderLineTracking()} {...styles.trackOrderLineButton}>
                                        {translate("Track")}
                                    </Button>
                                    <Modal
                                        headline={translate("Track Order Line")}
                                        isOpen={isTrackingModalOpen}
                                        handleClose={onClickTracking}
                                    >
                                        <OrderLineTrackingInfo
                                            shipmentPackages={order.shipmentPackages}
                                            orderLineId={orderLine.id}
                                        />
                                    </Modal>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
            {hasCustomProperties && (
                <GridItem {...styles.orderLineCustomPropertiesWrapperGridItem}>
                    <GridContainer {...styles.orderLineCustomPropertiesGridContainer}>
                        {orderLine.status && (
                            <GridItem {...styles.productInfoTextGridItem}>
                                <StyledWrapper {...styles.productInfoTextContent}>
                                    <Typography as="p" {...styles.productInfoHeadingAndTextLabel}>
                                        {productInfoHeadingAndTextLabel.statusLabel}
                                    </Typography>
                                    <Typography as="p">{orderLine.status}</Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {orderLine.warehouse && (
                            <GridItem {...styles.productInfoTextGridItem}>
                                <StyledWrapper {...styles.productInfoTextContent}>
                                    <Typography as="p" {...styles.productInfoHeadingAndTextLabel}>
                                        {productInfoHeadingAndTextLabel.dcWarehouseLabel}
                                    </Typography>
                                    <Typography as="p">{orderLine.warehouse}</Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {orderLine.properties.originalReleaseDate && (
                            <GridItem {...styles.productInfoTextGridItem}>
                                <StyledWrapper {...styles.productInfoTextContent}>
                                    <Typography as="p" {...styles.productInfoHeadingAndTextLabel}>
                                        {productInfoHeadingAndTextLabel.releaseDateLabel}
                                    </Typography>
                                    <Typography as="p">
                                        <LocalizedDateTime
                                            dateTime={new Date(orderLine.properties.originalReleaseDate)}
                                            options={{
                                                year: "numeric",
                                                month: "numeric",
                                                day: "numeric",
                                                timeZone: "UTC",
                                            }}
                                        />
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {orderLine.properties.inStoreDate && (
                            <GridItem {...styles.productInfoTextGridItem}>
                                <StyledWrapper {...styles.productInfoTextContent}>
                                    <Typography as="p" {...styles.productInfoHeadingAndTextLabel}>
                                        {productInfoHeadingAndTextLabel.inStoreDateLabel}
                                    </Typography>
                                    <Typography as="p">
                                        <LocalizedDateTime
                                            dateTime={new Date(orderLine.properties.inStoreDate)}
                                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                        />
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {orderLine.properties.releaseDate && (
                            <GridItem {...styles.productInfoTextGridItem}>
                                <StyledWrapper {...styles.productInfoTextContent}>
                                    <Typography as="p" {...styles.productInfoHeadingAndTextLabel}>
                                        {productInfoHeadingAndTextLabel.releaseDateLabel}
                                    </Typography>
                                    <Typography as="p">
                                        <LocalizedDateTime
                                            dateTime={new Date(orderLine.properties.releaseDate)}
                                            options={{
                                                year: "numeric",
                                                month: "numeric",
                                                day: "numeric",
                                                timeZone: "UTC",
                                            }}
                                        />
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {orderLine.properties.routingType && (
                            <GridItem {...styles.productInfoTextGridItem}>
                                <StyledWrapper
                                    {...(orderLine.properties.highlight === "False"
                                        ? styles.productInfoTextContentHighlighted
                                        : styles.productInfoTextContent)}
                                >
                                    <Typography as="p" {...styles.productInfoHeadingAndTextLabel}>
                                        {productInfoHeadingAndTextLabel.routingTypeLabel}
                                    </Typography>
                                    <Typography as="p">{orderLine.properties.routingType}</Typography>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {orderLine.properties.estimatedDeliveryDate && (
                            <GridItem {...styles.productInfoTextGridItem}>
                                <StyledWrapper {...styles.productInfoTextContent}>
                                    <Typography as="p" {...styles.productInfoHeadingAndTextLabel}>
                                        {productInfoHeadingAndTextLabel.estimatedDeliveryDateLabel}
                                    </Typography>
                                    <LocalizedDateTime
                                        dateTime={new Date(orderLine.properties.estimatedDeliveryDate)}
                                        options={{ year: "numeric", month: "numeric", day: "numeric", timeZone: "UTC" }}
                                    />
                                </StyledWrapper>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            )}
            <GridItem {...styles.productSubtotalWrapperGridItem}>
                <Typography as="p" {...styles.productInfoSubtotalHeading}>
                    {productInfoHeadingAndTextLabel.qtySubtotalLabel}:
                </Typography>
                <Typography as="p" {...styles.productInfoSubtotalPrice}>
                    &nbsp; {orderLine.totalRegularPriceDisplay}
                </Typography>
            </GridItem>
        </GridContainer>
        /* Store Custom Code - End */
    );
};
const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    vmiBinDetailsPageUrl,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    orderDetailsParameter, // Store Custom Code
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));
    const orderLines = order?.orderLines; // Store Custom Code

    if (!order || !order.orderLines) {
        return null;
    }

    /* Store Custom Code - Start */
    const filteredLines = orderLines?.filter(line => {
        if (!orderDetailsParameter) {
            return orderLines;
        }
        const searchRegex = new RegExp(orderDetailsParameter, "im");

        return (
            searchRegex.test(line.description) ||
            searchRegex.test(line.productName) ||
            searchRegex.test(line.customerName) ||
            searchRegex.test(line.productErpNumber)
        );
    });

    if (!filteredLines || filteredLines.length === 0) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <Typography {...styles.messageText}>{translate("No order items found")}</Typography>
            </StyledWrapper>
        );
    }

    const qtyOrderedLabel = translate("QTY Ordered");
    const qtyShippedLabel = translate("QTY Shipped");
    const qtySubtotalLabel = translate("QTY Subtotal");
    const unitOfMeasureLabel = translate("U Of M");
    const statusLabel = translate("Shipping Status");
    const dcWarehouseLabel = translate("DC (warehouse)");
    const releaseDateLabel = translate("Release Date");
    const inStoreDateLabel = translate("In Store Date");
    // eslint-disable-next-line spire/avoid-dynamic-translate
    const routingTypeLabel = translate("Routing Type");
    const estimatedDeliveryDateLabel = translate("Estimated Delivery Date");

    const _middleGridItems = [
        {
            text: translate("Price"),
            width: 3,
        },
        {
            text: qtyOrderedLabel,
            width: 3,
        },
        {
            text: qtyShippedLabel,
            width: 3,
        },
        {
            text: qtySubtotalLabel,
            width: 3,
        },
    ];

    const productInfoHeadingAndTextLabel = {
        qtyOrderedLabel,
        qtyShippedLabel,
        qtySubtotalLabel,
        unitOfMeasureLabel,
        statusLabel,
        dcWarehouseLabel,
        releaseDateLabel,
        inStoreDateLabel,
        routingTypeLabel,
        estimatedDeliveryDateLabel,
    };
    /* Store Custom Code - End */
    return (
        /* Store Custom Code - Start */
        <>
            <StyledWrapper {...styles.wrapperOrderLinesList}>
                <CartLinesListHeader
                    productsCount={filteredLines.length} // Starter Custom Code
                    isCondensed={false}
                    hideCondensedSelector={true}
                />
            </StyledWrapper>
            <GridContainer {...styles.gridContainer}>
                {filteredLines.map(orderLine => (
                    <CardContainer extendedStyles={styles.orderLineCardContainer} key={orderLine.id}>
                        <GridItem {...styles.orderLineCardGridItem}>
                            <OrderLineCard
                                orderLine={orderLine}
                                order={order}
                                wishListSettings={wishListSettings}
                                vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
                                styles={styles}
                                setAddToListModalIsOpen={setAddToListModalIsOpen}
                                addToWishList={addToWishList}
                                productInfoHeadingAndTextLabel={productInfoHeadingAndTextLabel}
                            />
                        </GridItem>
                    </CardContainer>
                ))}
            </GridContainer>
        </>
        /* Store Custom Code - End */
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
